import React, { useState } from 'react';
import { US_STATES } from '../lib/usStates';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, LogOut, AlertCircle } from 'lucide-react';
import { useUser, useAuthStore } from '../store/useAuthStore';
import { useTenantBranding } from '../hooks/useTenantBranding-runtime';
import { apiClient } from '../lib/api';

export const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { logout, getCurrentUser } = useAuthStore();
  const { branding } = useTenantBranding();

  const missingClinicName = !user?.profile?.clinicName;
  const missingClinicAddress = !user?.physicianData?.clinicAddress?.street;

  const [clinicName, setClinicName] = useState(user?.profile?.clinicName || '');
  const [address, setAddress] = useState({
    street: user?.physicianData?.clinicAddress?.street || '',
    street2: user?.physicianData?.clinicAddress?.street2 || '',
    city: user?.physicianData?.clinicAddress?.city || '',
    state: user?.physicianData?.clinicAddress?.state || '',
    zipCode: user?.physicianData?.clinicAddress?.zipCode || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (missingClinicName && !clinicName.trim()) {
      setError('Clinic name is required');
      return;
    }

    if (missingClinicAddress) {
      if (!address.street.trim() || !address.city.trim() || !address.state || !address.zipCode.trim()) {
        setError('Please fill in all required address fields');
        return;
      }
      if (!/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
        setError('Please enter a valid ZIP code');
        return;
      }
    }

    setSaving(true);
    try {
      const updateData: any = {};
      if (missingClinicName) updateData.clinicName = clinicName.trim();
      if (missingClinicAddress) updateData.clinicAddress = address;

      const result = await apiClient.updateUserProfile(user!._id, updateData);
      if (!result.success) {
        setError(result.error || 'Failed to update profile');
        return;
      }

      // Refresh user data so ProtectedRoute sees the updated profile
      await getCurrentUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--neutral-100)' }}>
      <header className="shadow-sm" style={{ background: 'var(--surface-primary)', borderBottom: '1px solid var(--neutral-200)' }}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg p-1" style={{ background: 'var(--surface-primary)' }}>
              <img
                src={branding.logo || "/5-axis-logo.svg"}
                alt={`${branding.companyName} Logo`}
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{branding.title}</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center pt-12 px-4">
        <div className="max-w-lg w-full rounded-xl shadow-lg p-8" style={{ background: 'var(--surface-primary)' }}>
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--brand-50, #eff6ff)' }}>
              <Building2 className="w-7 h-7" style={{ color: 'var(--brand-600, #2563eb)' }} />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Complete Your Profile</h1>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
              We need a few more details before you can access the platform.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: '#fef2f2', color: '#991b1b' }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {missingClinicName && (
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  Clinic Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                  <input
                    type="text"
                    value={clinicName}
                    onChange={(e) => setClinicName(e.target.value)}
                    placeholder="e.g. Coastal Medical & Vitality"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: 'var(--neutral-300)', color: 'var(--text-primary)' }}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {missingClinicAddress && (
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                  <MapPin className="w-4 h-4" />
                  Clinic Address <span className="text-red-500">*</span>
                </legend>
                <input
                  type="text"
                  value={address.street}
                  onChange={(e) => setAddress(a => ({ ...a, street: e.target.value }))}
                  placeholder="Street address"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--neutral-300)', color: 'var(--text-primary)' }}
                />
                <input
                  type="text"
                  value={address.street2}
                  onChange={(e) => setAddress(a => ({ ...a, street2: e.target.value }))}
                  placeholder="Suite, unit, etc. (optional)"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--neutral-300)', color: 'var(--text-primary)' }}
                />
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={address.city}
                    onChange={(e) => setAddress(a => ({ ...a, city: e.target.value }))}
                    placeholder="City"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: 'var(--neutral-300)', color: 'var(--text-primary)' }}
                  />
                  <select
                    value={address.state}
                    onChange={(e) => setAddress(a => ({ ...a, state: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: 'var(--neutral-300)', color: address.state ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
                  >
                    <option value="">State</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input
                    type="text"
                    value={address.zipCode}
                    onChange={(e) => setAddress(a => ({ ...a, zipCode: e.target.value }))}
                    placeholder="ZIP"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: 'var(--neutral-300)', color: 'var(--text-primary)' }}
                    maxLength={10}
                  />
                </div>
              </fieldset>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-60"
              style={{ background: 'var(--brand-600, #2563eb)' }}
            >
              {saving ? 'Saving...' : 'Continue to Dashboard'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};
